import { FixtureType } from '@ritabygglov/core';
import {
  CornerStairPlanIcon,
  DoorElevationIcon,
  DoorPlanIcon,
  DoubleDoorElevationIcon,
  DoubleDoorPlanIcon,
  DoubleStairPlanIcon,
  FixtureIconProps,
  RoofAngleIcon,
  SlidingDoorElevationIcon,
  SlidingDoorPlanIcon,
  StraightStairPlanIcon,
  UStairPlanIcon,
  WindowElevationIcon,
  WindowElevationOpenableIcon,
  WindowPlanIcon,
} from '../components/Icons';
import { roofAnglePitch } from '../helpers/roofAngleGeometry';

// ─── Snap geometry types (authoritative source — used by utility.ts) ──────────

export type NormalizedPoint = { fx: number; fy: number };
export type NormalizedSegment = {
  start: NormalizedPoint;
  end: NormalizedPoint;
};
export type FixtureSnapTemplate = {
  points: NormalizedPoint[];
  edges: NormalizedSegment[];
};

/**
 * For fixtures whose feature geometry is not at a fixed fraction of the
 * bounding box but depends on its proportions, the template is a function of
 * the fixture's size instead of a constant.
 */
export type FixtureSnapTemplateResolver = (size: {
  width: number;
  height: number;
}) => FixtureSnapTemplate;

export const resolveSnapTemplate = (
  template: FixtureSnapTemplate | FixtureSnapTemplateResolver,
  size: { width: number; height: number }
): FixtureSnapTemplate =>
  typeof template === 'function' ? template(size) : template;

export const NO_FIXTURE_SNAP: FixtureSnapTemplate = { points: [], edges: [] };

/**
 * DOOR_PLAN: the four jamb corners along the wall-opening line. The door stops
 * span x [0, 0.1] and [0.9, 1] of the width, and their room-facing edge sits at
 * y = 0.15 of the height (see DoorPlanIcon). These let a door snap its opening
 * to adjacent walls and elements.
 */
const DOOR_PLAN_SNAP: FixtureSnapTemplate = {
  points: [
    { fx: 0, fy: 0.15 },
    { fx: 0.1, fy: 0.15 },
    { fx: 0.9, fy: 0.15 },
    { fx: 1, fy: 0.15 },
  ],
  edges: [],
};

/**
 * ROOF_ANGLE: the sloped leg, running from the vertex at the bottom-left corner
 * up to its tip on the top edge (see RoofAngleIcon). Declared as an edge so
 * walls and lines can snap along the roof pitch. Because the leg is drawn the
 * same length as the base line rather than reaching the top-right corner, its
 * tip sits at cos(pitch) of the width — a function of the proportions, not a
 * fixed fraction — so this template has to be resolved per fixture. The vertex
 * itself is a bounding-box corner and already comes from the generic
 * contribution.
 */
const ROOF_ANGLE_SNAP: FixtureSnapTemplateResolver = ({ width, height }) => ({
  points: [],
  edges: [
    {
      start: { fx: 0, fy: 1 },
      end: { fx: Math.cos(roofAnglePitch(width, height)), fy: 0 },
    },
  ],
});

// ─── Fixture definition ───────────────────────────────────────────────────────

export type FixtureDefinition = {
  defaultWidth: number;
  defaultHeight: number;
  /** Icon component shared by both canvas rendering and the toolbar thumbnail. */
  icon: React.FC<FixtureIconProps>;
  /** ViewBox dimensions for the FixtureToolbarIcon wrapper, controlling thumbnail aspect ratio. */
  toolbarViewBoxWidth: number;
  toolbarViewBoxHeight: number;
  /** Stable DOM id passed to FixtureToolbarIcon (e.g. "toolbar-window-plan"). */
  toolbarId: string;
  /** i18n message id for the toolbar label. */
  i18nKey: string;
  /**
   * Interior/feature snap geometry in normalized local coordinates (0..1).
   * The 4 bounding-box corners + center are contributed generically by the
   * snap system; only declare interior feature points here.
   */
  snapTemplate: FixtureSnapTemplate | FixtureSnapTemplateResolver;
};

// ─── Registry ─────────────────────────────────────────────────────────────────

/**
 * Single source of truth for all fixture type metadata.
 * TypeScript's exhaustive Record check ensures every FixtureType variant has an
 * entry — adding a new variant to the enum immediately produces a compile error here.
 * Declaration order defines the toolbar display order.
 */
export const fixtureRegistry: Record<FixtureType, FixtureDefinition> = {
  [FixtureType.WINDOW_PLAN]: {
    defaultWidth: 56,
    defaultHeight: 8,
    icon: WindowPlanIcon,
    toolbarViewBoxWidth: 160,
    toolbarViewBoxHeight: 20,
    toolbarId: 'toolbar-window-plan',
    i18nKey: 'toolbar.fixtureWindowPlan',
    snapTemplate: NO_FIXTURE_SNAP,
  },
  [FixtureType.WINDOW_ELEVATION]: {
    defaultWidth: 48,
    defaultHeight: 48,
    icon: WindowElevationIcon,
    toolbarViewBoxWidth: 140,
    toolbarViewBoxHeight: 140,
    toolbarId: 'toolbar-window-elevation',
    i18nKey: 'toolbar.fixtureWindowElevation',
    snapTemplate: NO_FIXTURE_SNAP,
  },
  [FixtureType.WINDOW_ELEVATION_OPENABLE]: {
    defaultWidth: 48,
    defaultHeight: 48,
    icon: WindowElevationOpenableIcon,
    toolbarViewBoxWidth: 140,
    toolbarViewBoxHeight: 140,
    toolbarId: 'toolbar-window-elevation-openable',
    i18nKey: 'toolbar.fixtureWindowElevationOpenable',
    snapTemplate: NO_FIXTURE_SNAP,
  },
  [FixtureType.DOOR_PLAN]: {
    defaultWidth: 40,
    defaultHeight: 40,
    icon: DoorPlanIcon,
    toolbarViewBoxWidth: 140,
    toolbarViewBoxHeight: 140,
    toolbarId: 'toolbar-door-plan',
    i18nKey: 'toolbar.fixtureDoorPlan',
    snapTemplate: DOOR_PLAN_SNAP,
  },
  [FixtureType.DOOR_ELEVATION]: {
    defaultWidth: 40,
    defaultHeight: 80,
    icon: DoorElevationIcon,
    toolbarViewBoxWidth: 240,
    toolbarViewBoxHeight: 400,
    toolbarId: 'toolbar-door-elevation',
    i18nKey: 'toolbar.fixtureDoorElevation',
    snapTemplate: NO_FIXTURE_SNAP,
  },
  [FixtureType.DOUBLE_DOOR_PLAN]: {
    defaultWidth: 80,
    defaultHeight: 40,
    icon: DoubleDoorPlanIcon,
    toolbarViewBoxWidth: 140,
    toolbarViewBoxHeight: 70,
    toolbarId: 'toolbar-double-door-plan',
    i18nKey: 'toolbar.fixtureDoubleDoorPlan',
    snapTemplate: NO_FIXTURE_SNAP,
  },
  [FixtureType.DOUBLE_DOOR_ELEVATION]: {
    defaultWidth: 80,
    defaultHeight: 80,
    icon: DoubleDoorElevationIcon,
    toolbarViewBoxWidth: 240,
    toolbarViewBoxHeight: 400,
    toolbarId: 'toolbar-double-door-elevation',
    i18nKey: 'toolbar.fixtureDoubleDoorElevation',
    snapTemplate: NO_FIXTURE_SNAP,
  },
  [FixtureType.SLIDING_DOOR_PLAN]: {
    defaultWidth: 120,
    defaultHeight: 6,
    icon: SlidingDoorPlanIcon,
    toolbarViewBoxWidth: 140,
    toolbarViewBoxHeight: 20,
    toolbarId: 'toolbar-sliding-door-plan',
    i18nKey: 'toolbar.fixtureSlidingDoorPlan',
    snapTemplate: NO_FIXTURE_SNAP,
  },
  [FixtureType.SLIDING_DOOR_ELEVATION]: {
    defaultWidth: 40,
    defaultHeight: 80,
    icon: SlidingDoorElevationIcon,
    toolbarViewBoxWidth: 240,
    toolbarViewBoxHeight: 400,
    toolbarId: 'toolbar-sliding-door-elevation',
    i18nKey: 'toolbar.fixtureSlidingDoorElevation',
    snapTemplate: NO_FIXTURE_SNAP,
  },
  [FixtureType.ROOF_ANGLE]: {
    /* 52 / 104 = sin(30°) — the default box gives the conventional pitch. */
    defaultWidth: 104,
    defaultHeight: 52,
    icon: RoofAngleIcon,
    toolbarViewBoxWidth: 208,
    toolbarViewBoxHeight: 104,
    toolbarId: 'toolbar-roof-angle',
    i18nKey: 'toolbar.fixtureRoofAngle',
    snapTemplate: ROOF_ANGLE_SNAP,
  },
  [FixtureType.STRAIGHT_STAIR_PLAN]: {
    /* A flight a metre wide and 3.25 of them long, at the ~38 px/m of a 1:100
       plan, which the tread-to-width ratio turns into thirteen steps. The
       toolbar viewBoxes below are exact multiples of the defaults so that a
       thumbnail has the fixture's proportions, and so its step count. */
    defaultWidth: 40,
    defaultHeight: 130,
    icon: StraightStairPlanIcon,
    toolbarViewBoxWidth: 120,
    toolbarViewBoxHeight: 390,
    toolbarId: 'toolbar-straight-stair-plan',
    i18nKey: 'toolbar.fixtureStraightStairPlan',
    snapTemplate: NO_FIXTURE_SNAP,
  },
  [FixtureType.DOUBLE_STAIR_PLAN]: {
    defaultWidth: 80,
    defaultHeight: 130,
    icon: DoubleStairPlanIcon,
    toolbarViewBoxWidth: 240,
    toolbarViewBoxHeight: 390,
    toolbarId: 'toolbar-double-stair-plan',
    i18nKey: 'toolbar.fixtureDoubleStairPlan',
    snapTemplate: NO_FIXTURE_SNAP,
  },
  [FixtureType.CORNER_STAIR_PLAN]: {
    defaultWidth: 124,
    defaultHeight: 124,
    icon: CornerStairPlanIcon,
    toolbarViewBoxWidth: 372,
    toolbarViewBoxHeight: 372,
    toolbarId: 'toolbar-corner-stair-plan',
    i18nKey: 'toolbar.fixtureCornerStairPlan',
    snapTemplate: NO_FIXTURE_SNAP,
  },
  [FixtureType.U_STAIR_PLAN]: {
    defaultWidth: 80,
    defaultHeight: 130,
    icon: UStairPlanIcon,
    toolbarViewBoxWidth: 240,
    toolbarViewBoxHeight: 390,
    toolbarId: 'toolbar-u-stair-plan',
    i18nKey: 'toolbar.fixtureUStairPlan',
    snapTemplate: NO_FIXTURE_SNAP,
  },
};
